// Animate an image flying from a source element to the navbar cart icon.
// Includes: reduced-motion guard, queue/throttle to prevent overlap,
// aria-live announcement so screen readers know the cart updated.

let lastFireAt = 0;
let activeFlyers = 0;
const MIN_GAP_MS = 250; // throttle rapid clicks
const MAX_CONCURRENT = 3;

const LIVE_REGION_ID = "cart-live-region";

const ensureLiveRegion = (): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  let el = document.getElementById(LIVE_REGION_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = LIVE_REGION_ID;
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "true");
    el.setAttribute("role", "status");
    // visually hidden
    el.style.cssText =
      "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;";
    document.body.appendChild(el);
  }
  return el;
};

export const announceCartUpdate = (message: string) => {
  const region = ensureLiveRegion();
  if (!region) return;
  // Clear then set so repeated identical messages re-announce.
  region.textContent = "";
  window.setTimeout(() => {
    if (region) region.textContent = message;
  }, 30);
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const bumpCart = (target: HTMLElement) => {
  target.classList.remove("cart-bump");
  // force reflow so re-adding restarts the animation
  void target.offsetWidth;
  target.classList.add("cart-bump");
  window.setTimeout(() => target.classList.remove("cart-bump"), 500);
};

// Click feedback: emits 3 short lines above and 3 below the button, popping outward.
export const burstFromElement = (el: HTMLElement | null) => {
  if (!el || typeof document === "undefined") return;
  if (prefersReducedMotion()) {
    // Still give a quick press scale via class for non-motion users
    el.classList.remove("btn-press");
    void el.offsetWidth;
    el.classList.add("btn-press");
    window.setTimeout(() => el.classList.remove("btn-press"), 220);
    return;
  }
  el.classList.remove("btn-press");
  void el.offsetWidth;
  el.classList.add("btn-press");
  window.setTimeout(() => el.classList.remove("btn-press"), 260);

  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const positions = [
    { x: -18, y: -1, dir: "up" },
    { x: 0,   y: 0,  dir: "up" },
    { x: 18,  y: 1,  dir: "up" },
    { x: -18, y: 1,  dir: "down" },
    { x: 0,   y: 0,  dir: "down" },
    { x: 18,  y: -1, dir: "down" },
  ];
  for (const p of positions) {
    const line = document.createElement("span");
    const isUp = p.dir === "up";
    const baseY = isUp ? rect.top - 2 : rect.bottom + 2;
    line.style.cssText = `
      position: fixed;
      left: ${cx + p.x}px;
      top: ${baseY}px;
      width: 2px;
      height: 10px;
      background: hsl(var(--primary));
      border-radius: 2px;
      transform: translate(-50%, 0) rotate(${p.x * 0.6}deg);
      opacity: 0.9;
      pointer-events: none;
      z-index: 9999;
      transition: transform 360ms cubic-bezier(0.22,0.61,0.36,1), opacity 360ms ease-out;
      will-change: transform, opacity;
    `;
    document.body.appendChild(line);
    requestAnimationFrame(() => {
      const dy = isUp ? -14 : 14;
      line.style.transform = `translate(-50%, ${dy}px) rotate(${p.x * 0.6}deg) scaleY(1.4)`;
      line.style.opacity = "0";
    });
    window.setTimeout(() => line.remove(), 450);
  }
};


export interface FlyToCartOptions {
  announce?: string; // optional screen-reader message
}

/**
 * Fly an image from sourceEl to the navbar cart icon.
 * Returns true if animation was scheduled, false if throttled/skipped.
 */
export const flyToCart = (
  sourceEl: HTMLElement | null,
  imageUrl: string,
  options: FlyToCartOptions = {},
): boolean => {
  if (typeof window === "undefined") return false;

  const target = document.getElementById("navbar-cart-icon");
  if (target) bumpCart(target);

  if (options.announce) announceCartUpdate(options.announce);

  if (!sourceEl || !target || !imageUrl) return false;

  // Throttle rapid bursts
  const now = Date.now();
  if (now - lastFireAt < MIN_GAP_MS) return false;
  if (activeFlyers >= MAX_CONCURRENT) return false;
  lastFireAt = now;

  if (prefersReducedMotion()) return false;

  const srcRect = sourceEl.getBoundingClientRect();
  const tgtRect = target.getBoundingClientRect();
  if (srcRect.width === 0 || tgtRect.width === 0) return false;

  const size = Math.min(80, Math.max(40, srcRect.width));
  const startX = srcRect.left + srcRect.width / 2 - size / 2;
  const startY = srcRect.top + srcRect.height / 2 - size / 2;
  const endX = tgtRect.left + tgtRect.width / 2 - size / 2;
  const endY = tgtRect.top + tgtRect.height / 2 - size / 2;

  const flyer = document.createElement("img");
  flyer.src = imageUrl;
  flyer.alt = "";
  flyer.setAttribute("aria-hidden", "true");
  flyer.setAttribute("role", "presentation");
  flyer.dataset.flyToCart = "true";
  flyer.style.cssText = `
    position: fixed;
    left: ${startX}px;
    top: ${startY}px;
    width: ${size}px;
    height: ${size}px;
    object-fit: cover;
    border-radius: 9999px;
    box-shadow: 0 10px 30px hsl(43 52% 54% / 0.45);
    z-index: 9999;
    pointer-events: none;
    transition: transform 0.75s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.75s ease-out;
    will-change: transform, opacity;
  `;
  document.body.appendChild(flyer);
  activeFlyers++;

  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    activeFlyers = Math.max(0, activeFlyers - 1);
    flyer.remove();
  };

  // Animate on next frame to ensure transition applies
  requestAnimationFrame(() => {
    flyer.style.transform = `translate(${endX - startX}px, ${endY - startY}px) scale(0.18)`;
    flyer.style.opacity = "0.2";
  });

  flyer.addEventListener(
    "transitionend",
    () => {
      if (target) bumpCart(target);
      cleanup();
    },
    { once: true },
  );
  // Safety net in case transitionend never fires
  window.setTimeout(cleanup, 1500);
  return true;
};
